/**
 * AccountResolutionStep Component
 * Step 2 of the import wizard - match parsed accounts to existing accounts or create new
 * Supports both single-account (legacy) and multi-account documents
 *
 * WP-DOC-20: Added reprocess button and fallback to all accounts when suggestions empty
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import { AccountMatcher } from '@/components/documents/AccountMatcher';
import { DocumentAccountCard } from '@/components/documents/DocumentAccountCard';
import { CreateAccountModal } from '@/components/documents/CreateAccountModal';
import { AccountResolutionCard } from '@/components/documents/AccountResolutionCard';
import { useConfirmAccount, useResolveAccounts, useRetryDocument } from '@/hooks/useDocuments';
import { useAccounts } from '@/hooks/useAccounts';
import { ApiClientError } from '@/services/api';
import type {
  StagedImport,
  ConfirmAccountRequest,
  ParsedAccount,
  AccountResolution,
  MultiAccountStagedImport,
} from '@/types/document';

export interface AccountResolutionStepProps {
  stagedImport: StagedImport | MultiAccountStagedImport;
  onComplete: () => void;
  onCancel: () => void;
}

/**
 * Check if the staged import has multiple accounts
 */
function hasMultipleAccounts(
  stagedImport: StagedImport | MultiAccountStagedImport
): stagedImport is MultiAccountStagedImport {
  return (
    'parsedAccounts' in stagedImport &&
    Array.isArray(stagedImport.parsedAccounts) &&
    stagedImport.parsedAccounts.length > 1
  );
}

/**
 * Convert legacy single-account format to multi-account format
 */
function toMultiAccountFormat(stagedImport: StagedImport): ParsedAccount[] {
  const firstSuggestion = stagedImport.accountSuggestions[0];
  return [
    {
      id: 'default',
      institution: stagedImport.parsedAccount.institution,
      accountType: stagedImport.parsedAccount.accountType,
      accountMask: stagedImport.parsedAccount.accountMask,
      suggestedMatchId: firstSuggestion?.accountId ?? null,
      confidence: firstSuggestion?.confidence ?? 0,
      statementStart: stagedImport.parsedAccount.statementStart,
      statementEnd: stagedImport.parsedAccount.statementEnd,
      transactionCount: stagedImport.observationCount,
    },
  ];
}

export default function AccountResolutionStep({
  stagedImport,
  onComplete,
  onCancel,
}: AccountResolutionStepProps) {
  const navigate = useNavigate();

  // Determine if this is a multi-account import
  const isMultiAccount = hasMultipleAccounts(stagedImport);

  // Get parsed accounts (convert legacy format if needed)
  const parsedAccounts = useMemo(() => {
    if (isMultiAccount) {
      return (stagedImport as MultiAccountStagedImport).parsedAccounts ?? [];
    }
    return toMultiAccountFormat(stagedImport);
  }, [stagedImport, isMultiAccount]);

  // Fetch existing accounts for matching
  const { data: existingAccounts = [] } = useAccounts();

  // WP-DOC-20: Reprocess document hook
  const { mutateAsync: retryDocument, isPending: isReprocessing } = useRetryDocument();

  // State for account resolutions
  const [resolutions, setResolutions] = useState<Map<string, AccountResolution>>(() => {
    const initial = new Map<string, AccountResolution>();

    // Pre-populate with any existing resolutions
    if (
      isMultiAccount &&
      (stagedImport as MultiAccountStagedImport).accountResolutions
    ) {
      (stagedImport as MultiAccountStagedImport).accountResolutions?.forEach((r) => {
        initial.set(r.parsedAccountId, r);
      });
    }

    // For single account, check if already confirmed
    if (!isMultiAccount && stagedImport.confirmedAccountId) {
      initial.set('default', {
        parsedAccountId: 'default',
        action: 'link',
        linkedAccountId: stagedImport.confirmedAccountId,
      });
    }

    return initial;
  });

  const [expandedAccountId, setExpandedAccountId] = useState<string | null>(
    // Expand first unresolved account, or first account if all resolved
    parsedAccounts.find((a) => !resolutions.has(a.id))?.id ?? parsedAccounts[0]?.id ?? null
  );
  const [error, setError] = useState<string | null>(null);

  // For legacy single-account flow
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(() => {
    if (stagedImport.confirmedAccountId) {
      return stagedImport.confirmedAccountId;
    }
    const highConfidence = stagedImport.accountSuggestions.find((s) => s.confidence >= 0.8);
    return highConfidence?.accountId ?? null;
  });
  const [isCreatingNew, setIsCreatingNew] = useState(
    stagedImport.accountSuggestions.length === 0 && !stagedImport.confirmedAccountId
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [reprocessError, setReprocessError] = useState<string | null>(null);

  // Mutations
  const confirmMutation = useConfirmAccount();
  const resolveAccountsMutation = useResolveAccounts();

  // Calculate progress for multi-account
  const resolvedCount = resolutions.size;
  const totalAccounts = parsedAccounts.length;
  const allResolved = resolvedCount === totalAccounts;
  const progressPercent = totalAccounts > 0 ? (resolvedCount / totalAccounts) * 100 : 0;

  // Auto-advance to next unresolved account when one is resolved
  useEffect(() => {
    if (isMultiAccount && expandedAccountId && resolutions.has(expandedAccountId)) {
      const nextUnresolved = parsedAccounts.find((a) => !resolutions.has(a.id));
      if (nextUnresolved) {
        setExpandedAccountId(nextUnresolved.id);
      }
    }
  }, [isMultiAccount, expandedAccountId, resolutions, parsedAccounts]);

  // Handle resolution for multi-account
  const handleResolve = useCallback((resolution: AccountResolution) => {
    setResolutions((prev) => {
      const next = new Map(prev);
      next.set(resolution.parsedAccountId, resolution);
      return next;
    });
    setError(null);
  }, []);

  // Handle toggle expand
  const handleToggleExpand = useCallback((accountId: string) => {
    setExpandedAccountId((prev) => (prev === accountId ? null : accountId));
  }, []);

  // Legacy handlers for single-account flow
  const handleSelectAccount = useCallback((accountId: string) => {
    setSelectedAccountId(accountId);
    setIsCreatingNew(false);
    setError(null);
  }, []);

  const handleSelectCreateNew = useCallback(() => {
    setSelectedAccountId(null);
    setIsCreatingNew(true);
    setError(null);
  }, []);

  // Handle confirm button click
  const handleConfirm = useCallback(async () => {
    setError(null);

    if (isMultiAccount) {
      // Multi-account flow
      if (!allResolved) {
        setError('Please resolve all accounts before continuing.');
        return;
      }

      // Check if all accounts are skipped
      const allSkipped = Array.from(resolutions.values()).every((r) => r.action === 'skip');
      if (allSkipped) {
        setError('At least one account must be linked or created to continue.');
        return;
      }

      try {
        await resolveAccountsMutation.mutateAsync({
          stagedImportId: stagedImport.id,
          resolutions: Array.from(resolutions.values()),
        });
        onComplete();
      } catch (err) {
        if (err instanceof ApiClientError) {
          setError(err.detail);
        } else {
          setError('Failed to resolve accounts. Please try again.');
        }
      }
    } else {
      // Legacy single-account flow
      if (isCreatingNew) {
        setShowCreateModal(true);
        return;
      }

      if (!selectedAccountId) {
        setError('Please select an account or choose to create a new one.');
        return;
      }

      const request: ConfirmAccountRequest = {
        accountId: selectedAccountId,
      };

      try {
        await confirmMutation.mutateAsync({
          stagedImportId: stagedImport.id,
          request,
        });
        onComplete();
      } catch (err) {
        if (err instanceof ApiClientError) {
          setError(err.detail);
        } else {
          setError('Failed to confirm account. Please try again.');
        }
      }
    }
  }, [
    isMultiAccount,
    allResolved,
    resolutions,
    resolveAccountsMutation,
    stagedImport.id,
    onComplete,
    isCreatingNew,
    selectedAccountId,
    confirmMutation,
  ]);

  // Handle account created from modal (legacy flow)
  const handleAccountCreated = useCallback(
    async (accountId: string) => {
      setShowCreateModal(false);
      setSelectedAccountId(accountId);
      setIsCreatingNew(false);

      try {
        await confirmMutation.mutateAsync({
          stagedImportId: stagedImport.id,
          request: { accountId },
        });
        onComplete();
      } catch (err) {
        if (err instanceof ApiClientError) {
          setError(err.detail);
        } else {
          setError('Account created but failed to confirm. Please try again.');
        }
      }
    },
    [confirmMutation, stagedImport.id, onComplete]
  );

  const isLoading = confirmMutation.isPending || resolveAccountsMutation.isPending;
  const canProceed = isMultiAccount ? allResolved : selectedAccountId || isCreatingNew;

  // WP-DOC-20: Handle reprocess button click
  const handleReprocess = useCallback(async () => {
    setReprocessError(null);
    try {
      await retryDocument(stagedImport.documentId);
      // Navigate back to documents list after triggering reprocess
      navigate('/documents');
    } catch (err) {
      if (err instanceof ApiClientError) {
        setReprocessError(err.detail);
      } else {
        setReprocessError('Failed to reprocess document. Please try again.');
      }
    }
  }, [retryDocument, stagedImport.documentId, navigate]);

  // Render multi-account UI
  if (isMultiAccount) {
    return (
      <Card>
        <div className="space-y-6">
          {/* Header with progress */}
          <div>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Match Accounts</h2>
              <span className="text-sm text-gray-500">
                {resolvedCount} of {totalAccounts} resolved
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              We found {totalAccounts} accounts in your document. Please match each to an existing
              FlowIO account, create new accounts, or skip accounts you don&apos;t want to import.
            </p>

            {/* Progress bar */}
            <div className="mt-4">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                  role="progressbar"
                  aria-valuenow={resolvedCount}
                  aria-valuemin={0}
                  aria-valuemax={totalAccounts}
                  aria-label={`${resolvedCount} of ${totalAccounts} accounts resolved`}
                />
              </div>
            </div>
          </div>

          {/* Error messages */}
          {error && (
            <Alert variant="error" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}
          {reprocessError && (
            <Alert variant="error" onDismiss={() => setReprocessError(null)}>
              {reprocessError}
            </Alert>
          )}

          {/* Account resolution cards */}
          <div className="space-y-3">
            {parsedAccounts.map((parsedAccount) => {
              // Find suggestions for this account
              const accountSuggestions = stagedImport.accountSuggestions.filter(
                (s) => s.accountId
              );

              return (
                <AccountResolutionCard
                  key={parsedAccount.id}
                  parsedAccount={parsedAccount}
                  suggestions={accountSuggestions}
                  existingAccounts={existingAccounts}
                  resolution={resolutions.get(parsedAccount.id) ?? null}
                  onResolve={handleResolve}
                  isExpanded={expandedAccountId === parsedAccount.id}
                  onToggleExpand={() => handleToggleExpand(parsedAccount.id)}
                />
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <div className="flex gap-2">
              <Button variant="outline" onClick={onCancel} disabled={isLoading || isReprocessing}>
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={handleReprocess}
                disabled={isLoading || isReprocessing}
              >
                {isReprocessing ? 'Reprocessing...' : 'Reprocess Document'}
              </Button>
            </div>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!canProceed || isReprocessing}
              isLoading={isLoading}
            >
              Continue
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Render legacy single-account UI
  return (
    <>
      <Card>
        <div className="space-y-6">
          {/* Header */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Match Account</h2>
            <p className="mt-1 text-sm text-gray-600">
              We found account information in your document. Please match it to an existing FlowIO
              account or create a new one.
            </p>
          </div>

          {/* Error messages */}
          {error && (
            <Alert variant="error" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}
          {reprocessError && (
            <Alert variant="error" onDismiss={() => setReprocessError(null)}>
              {reprocessError}
            </Alert>
          )}

          {/* Parsed account info */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Document Account</h3>
            <DocumentAccountCard
              institution={stagedImport.parsedAccount.institution}
              accountType={stagedImport.parsedAccount.accountType}
              accountMask={stagedImport.parsedAccount.accountMask}
              dateRange={{
                start: stagedImport.parsedAccount.statementStart,
                end: stagedImport.parsedAccount.statementEnd,
              }}
            />
          </div>

          {/* Account matcher - WP-DOC-20: Pass all accounts as fallback when suggestions empty */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Match to:</h3>
            <AccountMatcher
              suggestions={stagedImport.accountSuggestions}
              allAccounts={existingAccounts}
              selectedAccountId={selectedAccountId}
              isCreatingNew={isCreatingNew}
              onSelectAccount={handleSelectAccount}
              onSelectCreateNew={handleSelectCreateNew}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <div className="flex gap-2">
              <Button variant="outline" onClick={onCancel} disabled={isLoading || isReprocessing}>
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={handleReprocess}
                disabled={isLoading || isReprocessing}
              >
                {isReprocessing ? 'Reprocessing...' : 'Reprocess Document'}
              </Button>
            </div>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!canProceed || isReprocessing}
              isLoading={isLoading}
            >
              {isCreatingNew ? 'Create Account' : 'Continue'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Create Account Modal */}
      <CreateAccountModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleAccountCreated}
        prefill={{
          institution: stagedImport.parsedAccount.institution,
          accountType: stagedImport.parsedAccount.accountType,
          accountMask: stagedImport.parsedAccount.accountMask,
        }}
      />
    </>
  );
}
